import React, { useEffect, useRef, useState } from 'react';
import ConfirmDialog from 'components/ConfirmDialog';

// Maximum number of independent Claude agents a single channel may have at
// once (backend/businesses/chat_business.py, MAX_AGENTS_PER_CHANNEL).
const MAX_AGENTS_PER_CHANNEL = 4;

/**
 * Channel member sidebar: currently-online users ("Online") plus the Claude
 * agents added to this channel ("Agents"), which stay listed regardless of
 * whether they are actively replying. The header menu adds a new agent
 * (auto-named "Claude N"); each agent row can be removed via a confirmation
 * dialog.
 */
const MemberList = ({ members, agents, onAddAgent, onRemoveAgent, menuDisabled = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [error, setError] = useState(null);
  const [agentPendingRemoval, setAgentPendingRemoval] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showMenu]);

  // The Game Master does not count towards MAX_AGENTS_PER_CHANNEL (backend/businesses/chat_business.py).
  const agentLimitReached = agents.filter((agent) => !agent.isGameMaster).length >= MAX_AGENTS_PER_CHANNEL;

  const handleAddAgent = async () => {
    setShowMenu(false);
    setError(null);
    try {
      await onAddAgent();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveAgent = async () => {
    setError(null);
    try {
      await onRemoveAgent(agentPendingRemoval.id);
      setAgentPendingRemoval(null);
    } catch (err) {
      setAgentPendingRemoval(null);
      setError(err.message);
    }
  };

  return (
    <div className="gh-box gh-chat-sidebar">
      <div className="gh-box-header d-flex align-items-center justify-content-between">
        <span>
          <i className="bi bi-people me-2" aria-hidden="true"></i>
          Members ({members.length + agents.length})
        </span>
        {!menuDisabled && (
          <div className="position-relative" ref={menuRef}>
            <button
              type="button"
              className="gh-channel-menu-btn"
              onClick={() => setShowMenu((prev) => !prev)}
              aria-label="Member menu"
              aria-expanded={showMenu}
            >
              <i className="bi bi-three-dots-vertical" aria-hidden="true"></i>
            </button>
            <ul
              className={`dropdown-menu dropdown-menu-end mt-1${showMenu ? ' show' : ''}`}
              style={{ top: '100%', right: 0 }}
            >
              <li>
                <button
                  type="button"
                  className="dropdown-item d-flex align-items-center"
                  onClick={handleAddAgent}
                  disabled={agentLimitReached}
                >
                  <i className="bi bi-robot me-2" aria-hidden="true"></i>
                  Add Claude agent
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 small gh-member-list-error" role="alert">
          <i className="bi bi-exclamation-octagon-fill" aria-hidden="true"></i>
          {error}
        </div>
      )}

      {members.length === 0 && agents.length === 0 ? (
        <div className="gh-box-body text-center text-muted-gh small">No one is online</div>
      ) : (
        <div className="gh-member-list-sections">
          <div className="gh-member-list-section-label text-muted-gh small">Online</div>
          {members.length === 0 ? (
            <div className="gh-member-list-empty text-muted-gh small">No one is online</div>
          ) : (
            <ul className="gh-member-list">
              {members.map((member) => (
                <li key={member.id} className="gh-member-list-item">
                  {member.displayName}
                </li>
              ))}
            </ul>
          )}

          <div className="gh-member-list-section-label text-muted-gh small">Agents</div>
          {agents.length === 0 ? (
            <div className="gh-member-list-empty text-muted-gh small">No agents added</div>
          ) : (
            <ul className="gh-member-list">
              {agents.map((agent) => (
                <li key={agent.id} className="gh-member-list-item d-flex align-items-center justify-content-between">
                  <span>
                    <i className={`bi ${agent.isGameMaster ? 'bi-shield-lock' : 'bi-robot'} me-2`} aria-hidden="true"></i>
                    {agent.name}
                  </span>
                  {/* The Game Master is required for the game to run and cannot be removed by a user. */}
                  {!agent.isGameMaster && (
                    <button
                      type="button"
                      className="gh-member-list-remove-btn"
                      onClick={() => setAgentPendingRemoval(agent)}
                      aria-label={`Remove ${agent.name}`}
                    >
                      <i className="bi bi-x-lg" aria-hidden="true"></i>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {agentPendingRemoval && (
        <ConfirmDialog
          title="Remove agent"
          message={<>Remove <strong>{agentPendingRemoval.name}</strong> from this channel?</>}
          confirmLabel="Remove"
          danger
          onConfirm={handleRemoveAgent}
          onCancel={() => setAgentPendingRemoval(null)}
        />
      )}
    </div>
  );
};

export default MemberList;
